import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import GenderToggle from "./GenderToggle";
import CardImage from "./CardImage";
import { useLocation, useNavigate } from "react-router-dom";

import { saveAs } from "file-saver";
import JSZip, { filter } from "jszip";
import { HttpUnAuthApi } from "../../interface/unauth-api";
import Toast from "../../utils/Toast";
import LabeledSelectBoxAuto from "./LabeledSelectBoxAuto";
import LabeledSelectBox from "./LabeledSelectBox";
import { useWindowSize } from "../../utils/useWindowSize";

interface propsType {
  // userState: userState;
  // selectedPeriod: string | null;
  // selectedKeywords: any;
  // selectedCategory: any;
  // briefList: any;
  // setBriefList: any;
  // queryText: string;
  // searchText: string;
  // setSearchText: any;
  // lastEvalKey: any;
  // setLastEvalKey: any;
  // nowPageSize: number;
  // setNowPageSize: any;
  lastScrollPos: number;
  setLastScrollPos: any;
  // needRefresh: boolean;
  // setNeedRefresh: any;
  // callback: any;
}

interface FilterState {
  imgtype: string;
  gender: string;
  season: string;
  city: string;
  designer: string;
}

interface RunwayLocalFilter extends FilterState {
  pageSize: number;
}

const unAuthAPI = new HttpUnAuthApi();
const pageSize = 12;

const DEFAULT_FIRST_FILTER = {
  key: "season",
  value: "2027 SPRING/SUMMER",
};

const RunwayList = (props:propsType) => {
  const toastRef: any = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const windowSize = useWindowSize();
  const isNarrow = (windowSize?.width ?? 0) <= 1023; // 1024 미만이면 모바일 취급

  const [openFilter, setOpenFilter] = useState(false); // 모바일일때만 사용가능
  
  const [runwayList, setRunwayList] = useState<any>();
  const [selectedRunways, setSelectedRunways] = useState<any>();
  const [filteredRunwayList, setFilteredRunwayList] = useState<any>([]);
  const [displayRunwayList, setDisplayRunwayList] = useState<any>([]);

  const [categories, setCategories] = useState<any>([]);
  const [firstFilter, setFirstFilter] = useState<{ key: string; value: string } | null>(DEFAULT_FIRST_FILTER);
  const [filters, setFilters] = useState<FilterState>({
    imgtype: "",
    season: DEFAULT_FIRST_FILTER.value,
    city: "",
    designer: "",
    gender: "",
  });
  const [isReadyToFetch, setIsReadyToFetch] = useState(false);
  const [lastEvalkey, setLastEvalKey] = useState<any>("");
  const [nowPageSize, setNowPageSize] = useState(pageSize);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isFirst, setIsFirst] = useState(true); // 페이지 되돌아올때 1번만 이전 스크롤 위치로 이동시키기 위함

  const scrollRef: any = useRef(0); // 뒤로가기 마지막 스크롤 저장
  //---------------------------------------------------------------------------------------
  // Scroll 처리 -> 스크롤 변경시 마지막 스크롤 위치를 ref에 저장 -> 페이지 이동전 마지막 스크롤 위치를 root에 전송
  // 최초 페이지 로딩시 마지막 스크롤로 이동
  //---------------------------------------------------------------------------------------
  useEffect(() => {
    // isFirst : 한번만 스크롤을 동기화 해야함. 이 설정이 없으면 브리프목록 불러오면서 스크롤 이동할때마다 계속 위로 올라감
    if (displayRunwayList.length > 0 && isFirst) {
      window.scrollTo({
        top: props.lastScrollPos, // 설정한 위치로 이동
      });
      setIsFirst(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRunwayList]);

  // 스크롤 변경시 최종위치 저장
  // State를 사용하지 않는 이유는 변경될때마다 렌더링이 일어나서 ref로 저장함
  const saveScrollPosition = () => {
    scrollRef.current = window.scrollY;
  };

  useEffect(() => {
    window.addEventListener("scroll", saveScrollPosition);
    return () => {
      window.removeEventListener("scroll", saveScrollPosition);
      props.setLastScrollPos(scrollRef.current); // 언마운트 : 즉 runway 상세보기 들어갈때 마지막 스크롤 위치를 저장
      setIsFirst(true);
    };
  }, []);

  // 메인에서 온 경우(플래그가 있으면) -> 리셋 (스크롤, 필터)
  useEffect(() => {
    const isFirstEntry = (location.state as any)?.resetFlag === true;

    // 👉 최초 진입
    if (isFirstEntry) {
      window.scrollTo({
        top: 0, // 설정한 위치로 이동
        behavior: "smooth", // 부드럽게 스크롤
      });

      props.setLastScrollPos(0);
      
      // 무조건 SPRING/SUMMER
      // setFirstFilter(DEFAULT_FIRST_FILTER);
      // setFilters((prev) => ({
      //   ...prev,
      //   season: DEFAULT_FIRST_FILTER.value,
      // }));

      setFirstFilter(DEFAULT_FIRST_FILTER);
      window.localStorage.setItem(
        "firstFilter",
        JSON.stringify(DEFAULT_FIRST_FILTER)
      );

      setFilters((prev) => {
        const nextFilters = {
          ...prev,
          season: DEFAULT_FIRST_FILTER.value,
        };

        const withPageSize = {
          ...nextFilters,
          pageSize: nowPageSize,
        };

        window.localStorage.setItem(
          "runwayFilter",
          JSON.stringify(withPageSize)
        );

        return nextFilters;
      });

      setIsReadyToFetch(true);

      // ⚠️ state 제거 (뒤로가기 시 다시 first로 오인하지 않도록)
      navigate(".", { replace: true, state: {} });
      return;
    }

    // 👉 이후 진입 (뒤로가기 등)
    const raw = window.localStorage.getItem("runwayFilter");
    const firstRaw = window.localStorage.getItem("firstFilter");

    if (!raw || !firstRaw) {
      setIsReadyToFetch(true);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<RunwayLocalFilter>;
      const firstParsed = JSON.parse(firstRaw) as { key: string; value: string };

      setFilters({
        imgtype: parsed.imgtype ?? "",
        season: parsed.season ?? DEFAULT_FIRST_FILTER.value,
        city: parsed.city ?? "",
        designer: parsed.designer ?? "",
        gender: parsed.gender ?? "",
      });

      setFirstFilter({
        key: firstParsed.key ?? DEFAULT_FIRST_FILTER.key,
        value: firstParsed.value ?? DEFAULT_FIRST_FILTER.value,
      });

      console.log("parsed.pageSize: ", parsed.pageSize);
      setNowPageSize(parsed.pageSize ?? pageSize);
    } catch (e) {
      console.error("runwayFilter 복원 실패:", e);
    } finally {
      setIsReadyToFetch(true);
    }
  }, [location.key]);

  useEffect(() => {
    const get_highlights_category_list = async () => {
      const param: any = {
        command: "get_highlights_category_list",
        pk: "highlights#category",
        sk: "all"
      };

      const res = await unAuthAPI.post(param);
      if (res.code === "200") {
        setCategories(res.response.category_list[0]);
      } else {
        console.error("[ERROR] get_highlights_category_list : ", res.response.error_msg);
      }
    };

    get_highlights_category_list();
    // get_runway_list();
    return () => {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    if (isAllSelected) {
      let temp: string[] = [...selectedRunways];

      filteredRunwayList.forEach((runway: any) => {
        const key = `${runway.season}#${runway.designer}`;
        if (!temp.includes(key)) {
          temp.push(key);
        }
      });

      setSelectedRunways(temp);
    } else {
      setSelectedRunways([]);
    }
  }, [isAllSelected]);


  useEffect(() => {
    if (!isReadyToFetch) return;
    get_runway_list();
  }, [isReadyToFetch, firstFilter]);

  useEffect(() => {
    console.log("selectedRunways: ", selectedRunways);
  }, [selectedRunways]);

  useEffect(() => {
    if (!runwayList) return; 
    
    const getFilteredRunwayList = () => {
      if (!runwayList) return [];

      // 필터에서 값이 있는 필드만 추출
      const activeFilters = Object.entries(filters).filter(
        ([_, value]) => value !== ''
      );

      // 필터 조건을 모두 만족하는 항목만 추출
      const filteredList = runwayList.filter((item: any) =>
        activeFilters.every(([key, value]) => item[key] === value)
      );

      // season__designer 조합으로 그룹핑
      const groupedMap = new Map<string, any>();

      for (const item of filteredList) {
        const key = `${item.season}__${item.designer}`;

        if (!groupedMap.has(key)) {
          // 최초 삽입
          groupedMap.set(key, { ...item, imglist: [...(item.imglist || [])] });
        } else {
          const existing = groupedMap.get(key);

          // imglist 통합 (중복 제거)
          const combinedImgList = Array.from(
            new Set([...(existing.imglist || []), ...(item.imglist || [])])
          );

          // 문자열 컬럼 병합
          const merged = {
            ...existing,
            ...item,
            imglist: combinedImgList,
            // 처럼 이어붙일 수 있는 필드들
            imgtype: [existing.imgtype, item.imgtype].filter(Boolean).join(','),
            gender: [existing.gender, item.gender].filter(Boolean).join(','),
            city: [existing.city, item.city].filter(Boolean).join(','),
          };

          groupedMap.set(key, merged);
        }
      }

      const dedupedList = Array.from(groupedMap.values());

      // const seen = new Set<string>();
      // const dedupedList = filteredList.filter((item: any) => {
      //   const key = `${item.seasons}__${item.designer}`;
      //   if (seen.has(key)) return false;
      //   seen.add(key);
      //   return true;
      // });

      console.log("dudepedList: ", dedupedList);

      // sk 기준 최신순 정렬      
      dedupedList.sort((a: any, b: any) => {
        if (a.sk > b.sk) return -1;
        if (a.sk < b.sk) return 1;
        return 0;
      });

      return dedupedList;
    }
    
    // if (firstFilter) {
      const filtered = getFilteredRunwayList();
      setFilteredRunwayList(filtered);
    // } else {
    //   setFilteredRunwayList(runwayList);
    // }
  }, [runwayList, filters]);

  useEffect(() => {
    if (lastEvalkey !== "") {
      get_runway_list_more();
    }
    // } else {
    //   get_runway_list();
    // }

    let temp: any = [];
    let inCount: number = 0;
    for (const info of filteredRunwayList) {
      if (inCount < nowPageSize) {
        temp.push(info);
        inCount += 1;
      }
    }
    setDisplayRunwayList(temp);
  }, [filteredRunwayList, nowPageSize]);

  // 런웨이 조회
  const get_runway_list = async () => {
    // init_state();
    const param: any = {
      command: "get_runway_list_page",
      index: firstFilter,
    };

    console.log("런웨이 조회 : ", param)

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      setRunwayList(res.response.runway_list);
      console.log("runwayList: ", res.response.runway_list);
      setLastEvalKey(res.response.last_eval_key);
    }
  };

  const get_runway_list_more = async () => {
    const param: any = {
      command: "get_runway_list_page",
      index: firstFilter,
      last_eval_key: lastEvalkey,
    };

    const res = await unAuthAPI.post(param);
    if (res.code === "200") {
      let temp: any = [...runwayList];
      for (const runway of res.response.runway_list) {
        temp.push(runway);
      }
      setRunwayList(temp);
      setLastEvalKey(res.response.last_eval_key);
    }
  };

  const update_runway_category = async (newGender: string) => {
    if (selectedRunways.length === 0) {
      toastRef.current?.toast("카테고리를 변경할 브리프가 없습니다.", "error", 4000, { vertical: "top", horizontal: "center" });
      return;
    }

    const param = {
      command: "update_runway_category",
      gender: newGender,
      runwayKeys: selectedRunways
    };

    const res = await unAuthAPI.post(param);
    if (res.code === "200"){
      // if (res.response.result) {
        window.location.reload();
        // await get_runway_list();
      // }
    } else {
      console.error(res.response.error_msg);
      toastRef.current?.toast("" + res.response.error_msg, "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
    }

    setSelectedRunways([]);
  }

  const goRunway = (key: string) => {
    const lastList: any = [];
    const [targetSeason, targetDesigner] = key.split('#');
    console.log("goRunway : ", key);

    for (const runway of filteredRunwayList) {
      if (runway.season === targetSeason && runway.designer === targetDesigner) {
        // if (!lastList.includes(runway.mainimg)) {
        //   if (runway.mainimg) lastList.push(runway.mainimg);
        // }
        if (runway.imglist) lastList.push(...runway.imglist);
      }
    }

    window.localStorage.setItem("selectedImgList", lastList);

    const encodedSeason = encodeURIComponent(targetSeason);
    navigate(`/runway/${encodedSeason}/${targetDesigner}`);
  };

  const selectRunway = (key: string) => {
    let temp = [...selectedRunways];
    const index = temp.indexOf(key);
    if (index > -1) {
      temp.splice(index, 1);
    } else {
      temp.push(key);
    }
    setSelectedRunways(temp);
  };

  const selectAllRunway = () => {
    setIsAllSelected(!isAllSelected);
  };

  const resetFilters = () => {
    setFilters({
      imgtype: '',
      season: '',
      city: '',
      designer: '',
      gender: '',
    });
    setFirstFilter(null);
    setNowPageSize(pageSize);
    window.localStorage.removeItem("runwayFilter");
    window.localStorage.removeItem("firstFilter");
  };

  const updateFilter = (key: keyof FilterState, value: string) => {
    /* 1️⃣ filters 업데이트 */
    setFilters((prev) => {
      const nextFilters = {
        ...prev,
        [key]: value,
      };

      const withPageSize = {
        ...nextFilters,
        pageSize: nowPageSize,
      };

      window.localStorage.setItem(
        "runwayFilter",
        JSON.stringify(withPageSize)
      );

      return nextFilters;
    });

    /* 2️⃣ firstFilter 업데이트 규칙 */
    if (value) {
      // 값이 있으면 → 무조건 이 필터가 대표
      const nextFirstFilter = { key, value };

      setFirstFilter(nextFirstFilter);
      window.localStorage.setItem(
        "firstFilter",
        JSON.stringify(nextFirstFilter)
      );
    } else {
      // 값이 비워졌고, 그게 현재 대표라면 제거
      if (firstFilter?.key === key) {
        setFirstFilter(null);
        window.localStorage.removeItem("firstFilter");
      }
    }
  };

  // const fncDownloadAttach = async (imgUrl: any) => {
  //   await fetch(imgUrl, { method: "GET" })
  //     .then((res) => {
  //       return res.blob();
  //     })
  //     .then((blob) => {
  //       saveAs(blob, `${imgUrl}`);
  //     })
  //     .catch((err) => {
  //       console.error("err : ", err);
  //     });
  // };

  const sanitizeFileName = (input: string): string => {
    // 파일명에 사용 불가 문자를 안전하게 치환
    return input.replace(/[\/\\:*?"<>|]/g, "_");
  };

  const fnDownloadCount = async () => {
    if (!Array.isArray(selectedRunways) || selectedRunways.length === 0) return;
  
    // 요청 배열 생성 - 시즌, 디자이너 같이 들어감.
    const requests = selectedRunways.map((selectedKey: string) => {
      const param = {
        command: "update_download_count",
        selectedKey: selectedKey,
      };
      return unAuthAPI.post(param);
    });
    const results = await Promise.allSettled(requests);
    const flatData = results
      .filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled")
      .flatMap((r) => r.value?.response?.result ?? []);
  
    return flatData;
  };

  const fncDownloadAttach = async () => {
    
    if (selectedRunways.length === 0) {
      toastRef.current?.toast("다운로드할 이미지가 없습니다.", "error", 4000, { vertical: "top", horizontal: "center" });
      return;
    }
    try {
      if (!selectedRunways) return;
      await Promise.all(
        selectedRunways.map(async (selectedKey: string) => {
          console.log("selectedKey: ", selectedKey);
          console.log("displayRunwayList: ", displayRunwayList);
          // season과 designer 추출
          const [season, designer] = selectedKey.split("#");
          const safeSeason = sanitizeFileName(season);
          const safeDesigner = sanitizeFileName(designer);

          // displayRunwayList에서 해당 runway 찾기
          const runway = displayRunwayList.find(
            (item: any) => item.season === season && item.designer === designer
          );

          if (!runway || !runway.imglist || runway.imglist.length === 0) return;

          const zip = new JSZip();

          // 각 이미지 다운로드
          await Promise.all(
            runway.imglist.map(async (imgUrl: string, index: number) => {
              const res = await fetch(imgUrl);
              if (!res.ok) throw new Error(`Failed to fetch: ${imgUrl}`);

              const blob = await res.blob();
              const fileName = `${safeSeason}_${safeDesigner}_${String(index + 1).padStart(2, "0")}.jpg`;

              // zip 내에 폴더별로 정리 (선택사항)
              zip.file(fileName, blob);
            })
          );

          const zipBlob = await zip.generateAsync({ type: "blob" });
          const zipFileName = `${safeSeason}_${safeDesigner}.zip`;

          saveAs(zipBlob, zipFileName);
        })
      );
      // 다운로드 카운트 증가.
      fnDownloadCount()
    } catch (error) {
      console.error("ZIP 다운로드 중 오류 발생: ", error);
    }
  };

  const toPrintSafeURL = (url: string) => {
    // 이미 인코딩된 %를 이중 인코딩하지 않도록 encodeURI 사용
    let u = encodeURI(url);
    // #가 있으면 브라우저가 fragment로 처리 → 실제 요청 실패 가능
    u = u.replace(/#/g, "%23");
    return u;
  };

  type PrintGroup = { title: string; urls: string[] };
  
  const buildCombinedPrintHTML = (groups: PrintGroup[]) => `
  <!doctype html>
  <html>
  <head>
  <meta charset="utf-8" />
  <title>AI TB</title>
  <style>
    @page { size: A4 portrait; margin: 12mm; }
    html, body { margin: 0; padding: 0; }
    /* 인쇄 가능 영역: 186×273mm */
    .page {
      width: 186mm;
      height: 273mm;
      box-sizing: border-box;
      display: block;
      break-after: page;            /* 최신 속성 */
      page-break-after: always;     /* 구형 브라우저 보강 */
      overflow: hidden;             /* 오버플로우로 다음 페이지 침범 방지 */
    }

    .title {
      width: 186mm; height: 273mm;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Arial;
    }
    .title h1 { margin: 0 0 8mm; font-size: 24pt; }
    .title p  { margin: 0; font-size: 12pt; color: #555; }

    /* 이미지+캡션 2단 레이아웃 */
    .figure {
      display: flex;
      flex-direction: column;
      height: 100%;      /* .page를 꽉 채움 */
      min-height: 0;     /* flex overflow 방지 */
    }
    .img-area {
      flex: 1;           /* 남은 높이 전부 */
      min-height: 0;     /* 중요: 자식 overflow로 캡션 밀림 방지 */
      display: flex;
      align-items: center;
      justify-content: center;
    }
    img.print-img {
      max-width: 100%;
      max-height: 100%;  /* img-area 내부에서만 스케일 */
      object-fit: contain;
      display: block;
    }
    .caption {
      height: 12mm;           /* 항상 확보되는 캡션 높이 */
      line-height: 12mm;      /* 수직 중앙 */
      text-align: center;
      font-size: 11pt;
      color: #333;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Noto Sans, Arial;
      box-sizing: border-box;
    }

    /* 혹시 마진 붕괴 대비 */
    .caption, .title { margin: 0; }
  </style>
  </head>
  <body>
    ${groups.map(g => `
      <div class="page title">
        <h1>${g.title}</h1>
        <p>Generated ${new Date().toLocaleDateString("ko-KR")}</p>
      </div>
      ${g.urls.slice().sort((a, b) => a.localeCompare(b)).map((u, idx) => {
          return `
            <div class="page">
              <div class="figure">
                <div class="img-area">
                  <img class="print-img" src="${toPrintSafeURL(u)}" />
                </div>
                <div class="caption">${g.title}</div>
              </div>
            </div>
          `;
      }).join("")}
    `).join("")}
  </body>
  </html>`;



  // 2) iframe 내부 이미지 로드 대기
  const waitAllImagesLoaded = (doc: Document) => {
    const imgs = Array.from(doc.images) as HTMLImageElement[];
    if (imgs.length === 0) return Promise.resolve();
    return Promise.all(imgs.map(img => new Promise<void>((res, rej) => {
      if (img.complete && img.naturalWidth > 0) return res();
      img.addEventListener("load", () => res(), { once: true });
      img.addEventListener("error", () => rej(new Error(`이미지 로드 실패: ${img.src}`)), { once: true });
    }))).then(() => undefined);
  };

 

  const fncPrint = async () => {
    if (!selectedRunways || selectedRunways.length === 0) {
      toastRef.current?.toast("인쇄할 이미지가 없습니다.", "error", 4000, {
        vertical: "top", horizontal: "center",
      });
      return;
    }
    if (selectedRunways.length !== 1) {
      toastRef.current?.toast("인쇄는 한 번에 하나의 런웨이만 가능합니다. 한 개만 선택해주세요.", "warning", 4000, {
        vertical: "top", horizontal: "center",
      });
      return;
    }
  
    // 1) 선택한 키들로 그룹(표지+이미지들) 만들기
    const [season, designer] = selectedRunways[0].split("#");
    const runway = displayRunwayList.find(
      (it: any) => it.season === season && it.designer === designer
    );
    if (!runway || !Array.isArray(runway.imglist) || runway.imglist.length === 0) {
      console.warn(`[PRINT] 이미지 없음: ${season}_${designer}`);
      toastRef.current?.toast("선택한 런웨이에 이미지가 없습니다.", "error", 4000, {
        vertical: "top", horizontal: "center",
      });
      return;
    }

    const groups: PrintGroup[] = [
      {
        title: `${season}_${designer}`,
        urls: runway.imglist,
      },
    ];
  
    // 2) 한 번에 하나의 문서(iframe)로 작성 → 단 한 번 print()
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
  
    try {
      const win = iframe.contentWindow!;
      const doc = iframe.contentDocument || win.document;
  
      doc.open();
      doc.write(buildCombinedPrintHTML(groups));
      doc.close();
  
      // 로드 완료 후 인쇄 (네가 만든 waitAllImagesLoaded 재사용)
      await new Promise<void>((resolve) => {
        if (doc.readyState === "complete") resolve();
        else win.addEventListener("load", () => resolve(), { once: true });
      });
      await waitAllImagesLoaded(doc);
  
      win.focus();
      win.print();
    } catch (e) {
      console.error("인쇄 중 오류:", e);
      toastRef.current?.toast("인쇄 중 오류가 발생했습니다.", "error", 4000, {
        vertical: "top", horizontal: "center",
      });
    } finally {
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }
  };

  // 컴포넌트 내부(리턴문 위)에 추가
  const renderFilterBody = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",        // 부모 높이 채우기 (Dialog/사이드바 어디서든)
        maxHeight: "inherit",
      }}
    >
      {/* 헤더: 항상 상단 고정 */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          // 헤더 패딩
          p: "20px 24px 12px 24px",
          backgroundColor:"#fff",
          borderRadius:"16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "20px" }}>필터</Typography>
            <img src="/images/icon-filter.png" alt="filter" style={{ width: 20, height: 20 }} />
          </Box>
  
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
            onClick={resetFilters}
          >
            <img src="/images/icon-arr-re.png" style={{ width: "9px", height: "9px" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>초기화</Typography>
          </Box>
        </Box>
      </Box>
  
      {/* 셀렉트 영역: 여기만 스크롤 */}
      <Box
        sx={{
          flex: 1,            // 남은 공간 모두 차지
          minHeight: 0,       // flex 컨테이너에서 스크롤 되게 하는 핵심
          overflowY: "auto",  // 스크롤은 여기서만
          scrollbarGutter: "stable",     
          // 본문 패딩
          p: "16px 24px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <LabeledSelectBox
          placeholder="IMAGE TYPE"
          value={filters.imgtype}
          onChange={(val) => updateFilter("imgtype", val)}
          options={[
            { label: "전체", value: "" },
            ...(categories.imgtype?.map((imgtype: string) => ({
              label: imgtype.toUpperCase(),
              value: imgtype,
            })) || []),
          ]}
        />
  
        <LabeledSelectBox
          placeholder="GENDER"
          value={filters.gender}
          onChange={(val: any) => updateFilter("gender", val)}
          options={[
            { label: "전체", value: "" },
            ...(categories.gender?.map((gender: string) => ({
              label: gender.toUpperCase(),
              value: gender,
            })) || []),
          ]}
        />
  
        <LabeledSelectBoxAuto
          placeholder="SEASON"
          value={filters.season}
          onChange={(val) => updateFilter("season", val)}
          options={[
            { label: "전체", value: "" },
            ...(categories.season
              ?.slice()
              .reverse()
              .map((season: string) => ({ label: season.toUpperCase(), value: season })) || []),
          ]}
        />
  
        <LabeledSelectBox
          placeholder="CITY"
          value={filters.city}
          onChange={(val) => updateFilter("city", val)}
          options={[
            { label: "전체", value: "" },
            { label: "NEWYORK", value: "NEWYORK" },
            { label: "LONDON", value: "LONDON" },
            { label: "MILAN", value: "MILAN" },
            { label: "PARIS", value: "PARIS" },
            { label: "COPENHAGEN", value: "COPENHAGEN" },
            ...(categories.city
              ?.filter((city: any) =>
                !["NEWYORK", "LONDON", "MILAN", "PARIS", "COPENHAGEN"].includes(city.toUpperCase())
              )
              .slice()
              .reverse()
              .map((city: string) => ({ label: city.toUpperCase(), value: city })) || []),
          ]}
        />
  
        <LabeledSelectBoxAuto
          placeholder="DESGINER"
          value={filters.designer}
          onChange={(val) => updateFilter("designer", val)}
          options={[
            { label: "전체", value: "" },
            ...(categories.designer?.map((designer: string) => ({
              label: designer.toUpperCase(),
              value: designer,
            })) || []),
          ]}
        />
      </Box>
    </Box>
  );
  


  return (
  <>
    <section id="commWrap" style={{maxWidth:'1250px'}}>
      <article className="article-01">
        <div className="article-tit" style={{ marginBottom: '80px' }}>
          RUNWAY DATA
        </div>
        <Stack direction="row" gap={3}>
          {!isNarrow &&
            <Box>
              <Box
                sx={{
                  maxWidth: "260px",
                  backgroundColor: "#FFFFFF",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0px 8px 20px 0px rgba(0, 0, 0, 0.1)",
                  border: '1px solid #2E3341',
                  borderRadius: "16px", // 선택 사항: 더 둥글게 하고 싶으면
                  paddingBottom:"50px",
                }}
              >
                {renderFilterBody()}
              </Box>
            </Box>
            }

          <Stack direction="column" gap={3} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', width: '100%' }}>
              {isNarrow && (
                <div
                  style={{ width: 25, height: 25, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onClick={() => setOpenFilter(true)}
                  aria-label="open-filter"
                  title="필터 열기"
                >
                  <img src="/images/icon-filter.png" alt="filter" style={{ width: 20, height: 20 }} />
                </div>
              )}
              {/* {!isNarrow&&(
                <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
                  <div
                    style={{ 
                      width: '25px',
                      height: '25px',
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`checkImgAll`}
                      className="check-brief"
                      onChange={selectAllRunway}
                      checked={isAllSelected}
                      style={{ cursor: 'pointer', display: 'none' }}
                    />
                    <label 
                      htmlFor={`checkImgAll`}
                      style={{ display: 'block', width: '100%', height: '100%', cursor: 'pointer' }}
                    ></label>
                  </div>
                  <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>전체 선택</Typography>
                </div>
              )} */}

              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                {filters.gender===""?<></>:
                  <Box 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor:'pointer'}}
                  onClick={() => {
                    const newGender = filters.gender !== 'men' ? 'men' : 'women';
                    update_runway_category(newGender);
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>{filters.gender !== 'men' ? '남성복으로 복사' : '남성복에서 삭제'}</Typography>
                  <img src={filters.gender !== 'men' ? "/images/icon-copy.png" : "/images/icon-uncopy.png"} style={{ width: '18px', height: '18px' }}/>
                </Box>
                }
                
                {!isNarrow&&
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor:'pointer'}}
                    onClick={fncPrint}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>인쇄하기</Typography>
                    <img src="/images/icon-print-18.png" style={{ width: '18px', height: '18px' }}/>
                  </Box>
                }
                

                <Box 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor:'pointer'}} 
                  onClick={fncDownloadAttach}  
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '16px' }}>다운로드</Typography>
                  <img src="/images/icon-download-18.png" style={{ width: '18px', height: '18px' }}/>
                </Box>
              </Box>
            </Box>

            <div style={{ width: '100%' }}>
              <dl>
                <dd
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  {displayRunwayList && displayRunwayList.length > 0 ? (
                    <>
                      {displayRunwayList.map((info: any, index: number) => (
                        <CardImage
                          key={index}
                          index={index}
                          runwayInfo={info}
                          imgtype={filters.imgtype}
                          checked={selectedRunways.indexOf(`${info.season}#${info.designer}`) > -1}
                          callback={(payload: any) => {
                            if (payload.command === "selectRunway") {
                              selectRunway(payload.key);
                            } else if (payload.command === "goRunway") {
                              goRunway(payload.key);
                            }
                          }}
                        />
                      ))}
                      
                      {/* 간격을 위한 더미 채우기 */}
                      {(() => {
                        const cols = windowSize.width >= 1024 ? 4 : windowSize.width >= 640 ? 3 : 2;
                        const cardWidth = windowSize.width >= 1024 ? "23%"
                            : windowSize.width >= 640
                            ? "31.3%"
                            : "48%";
                        const len = displayRunwayList?.length ?? 0;
                        const remainder = len % cols;
                        const dummyCount = remainder === 0 ? 0 : cols - remainder;
                        return Array.from({ length: dummyCount }).map((_, i) => (
                          <div
                            key={`dummy-${i}`}
                            style={{
                              width: cardWidth,
                              marginBottom: '48px',
                              visibility: 'hidden',
                            }}
                          />
                        ));
                      })()}
                    </>
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        textAlign: 'center',
                        padding: '60px 20px',
                        color: '#999',
                        fontSize: '16px',
                      }}
                    >
                      데이터가 없습니다.
                    </div>
                  )}
                </dd>
              </dl>

              {nowPageSize < filteredRunwayList.length && (
                <>
                <div className="btn-more-list-wrap" >
                  <div className="btn-more-list"
                    style={{bottom: isNarrow?'15px':"30px"}}
                    >
                    <button
                      // variant="text"
                      onClick={() => {
                        setNowPageSize((prev) => {
                          const next = prev + pageSize;
                          // 로컬스토리지에 추가저장.
                          const newFilters = { ...filters, pageSize: next };
                          window.localStorage.setItem("runwayFilter", JSON.stringify(newFilters));
                          return next;
                        });
                      }}
                      style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 1, 
                        fontSize: "16px", 
                        fontWeight: "800",
                      }}
                    >
                      <span>이미지 더보기</span>
                      <i style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "33px", height: "33px", marginLeft: "0px" }}>
                        <img src="/images/btn-add-list.png" style={{ width: "13px", height: "13px" }} />
                      </i>
                    </button>
                  </div>
                  <div className="gradient-bottom"
                    style={{bottom: isNarrow?'15px':"30px"}}
                  />
                </div>
                </>
              )}
            </div>
          </Stack>
        </Stack>
      </article>
    </section>
    <Toast ref={toastRef} />  
    {/* ✅ 모바일 전용 필터 모달 */}
    {isNarrow && (
      <Dialog
        open={openFilter}
        onClose={() => setOpenFilter(false)}
        PaperProps={{
          sx: {
            width: "90%",       // 화면의 90% 폭
            maxWidth: "400px",  // 최대 400px
            maxHeight: "80vh",    // ✅ 최대 80% (뷰포트)
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px 8px 20px 0px rgba(0, 0, 0, 0.1)",
            borderRadius: "16px",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* 사이드바와 동일한 본문을 재사용 */}
          {renderFilterBody()}
        </DialogContent>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 2 }}>
          <Box
            flex={2}
            style={{
              height: "60px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: 700,
              borderRadius: "30px",
              fontSize: "1rem",
              transition: "ease 0.3s 0s",
              backgroundColor: "rgba(209, 209, 209, 1)",
              color: "rgba(42, 42, 42, 1)",
              gap: "4px",
            }}
            onClick={()=>setOpenFilter(false)}
          >
            <span style={{ fontWeight: 700, fontSize: "16px" }}>취소</span>
          </Box>
          <Box
            flex={3}
            style={{
              height: "60px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: 700,
              borderRadius: "30px",
              fontSize: "1rem",
              transition: "ease 0.3s 0s",
              backgroundColor: "rgba(208, 255, 0, 1)",
              color: "rgba(42, 42, 42, 1)",
              gap: "4px",
            }}
            onClick={() => setOpenFilter(false)}
          >
              <span style={{ fontWeight: 700, fontSize: "16px" }}>적용하기</span>
          </Box>

        </Box>
      </Dialog>
    )}


  </>
  )
}

export default RunwayList;